import { useSelector } from "react-redux";

/**
 * useSecondarySalesData Hook
 * Fetches secondary sales related data from Redux store
 * 
 * @returns {Object} Secondary sales data including sales, target, and trends
 */
export const useSecondarySalesData = () => {
  const secondarySales = useSelector(
    (state) => state?.dashBoardDetails?.secondarySalesAcheived?.[0]?.sum,
    (prev, next) => prev === next
  );
  const plannedData = useSelector(
    (state) => state?.dashBoardDetails?.trend,
    (prev, next) => prev?.length === next?.length
  );
  const targetAcheived = useSelector(
    (state) => state?.dashBoardDetails?.secondarySalesTagetAcheived?.[0]?.per,
    (prev, next) => prev === next
  );
  const targetPlannedData = useSelector(
    (state) => state?.dashBoardDetails?.targetTrend,
    (prev, next) => prev?.length === next?.length
  );

  return {
    secondarySales,
    plannedData,
    targetAcheived,
    targetPlannedData,
  };
};

/**
 * useSalesOperationsData Hook
 * Fetches sales operations related data from Redux store
 * 
 * @returns {Object} Sales operations data including outlets billed and RSM data
 */
export const useSalesOperationsData = () => {
  const outletsBilledFtmCount = useSelector(
    (state) => state?.dashBoardDetails?.outletsBilledFtmCount?.[0]?.count,
    (prev, next) => prev === next
  );
  const outletsBilledFtmCountTrend = useSelector(
    (state) => state?.dashBoardDetails?.outletsBilledFtmCountTrend,
    (prev, next) => prev?.length === next?.length
  );
  const rsmFtmSum = useSelector(
    (state) => state?.dashBoardDetails?.rsmFtmSum?.[0]?.sum,
    (prev, next) => prev === next
  );
  const rsmFtmSumTrend = useSelector(
    (state) => state?.dashBoardDetails?.rsmFtmSumTrend,
    (prev, next) => prev?.length === next?.length
  );
  const rsmFtmRsi = useSelector(
    (state) => state?.dashBoardDetails?.rsmFtmSum?.[0]?.rsi,
    (prev, next) => prev === next
  );

  return {
    outletsBilledFtmCount,
    outletsBilledFtmCountTrend,
    rsmFtmSum,
    rsmFtmSumTrend,
    rsmFtmRsi,
  };
};

/**
 * useTeamData Hook
 * Fetches team related data from Redux store
 * 
 * @returns {Object} Team data including TSI/TSR scores, incentive, and SOP
 */
export const useTeamData = () => {
  const teamsAvgTsiScore = useSelector(
    (state) => state?.dashBoardDetails?.teamsAverageTsiScore?.[0]?.avg,
    (prev, next) => prev === next
  );
  const teamsAvgTsrScore = useSelector(
    (state) => state?.dashBoardDetails?.teamsAverageTsrScore?.[0]?.avg,
    (prev, next) => prev === next
  );
  const teamIncentive = useSelector(
    (state) => state?.dashBoardDetails?.teamsIncentive,
    (prev, next) => prev?.[0]?.counts === next?.[0]?.counts && prev?.[0]?.total === next?.[0]?.total
  );
  const teamSop = useSelector(
    (state) => state?.dashBoardDetails?.teamsSop,
    (prev, next) => prev?.[0]?.counts === next?.[0]?.counts && prev?.[0]?.total === next?.[0]?.total
  );

  return {
    teamsAvgTsiScore,
    teamsAvgTsrScore,
    teamIncentive,
    teamSop,
  };
};

/**
 * useWssData Hook
 * Fetches WSS related data from Redux store
 * 
 * @returns {Object} WSS data including auto value, sync exceptions, and block count
 */
export const useWssData = () => {
  const wssAutoValueFtdSum = useSelector(
    (state) => state?.dashBoardDetails?.wssAutoValueFtd?.[0]?.sum,
    (prev, next) => prev === next
  );
  const wssDarpanSyncDayExceptionCount = useSelector(
    (state) =>
      state?.dashBoardDetails?.wssDarpanSyncDayExceptionCount?.[0]?.count,
    (prev, next) => prev === next
  );
  const wssWssInBlockCount = useSelector(
    (state) => state?.dashBoardDetails?.wssWssInBlockCount?.[0]?.count,
    (prev, next) => prev === next
  );
  const wssAutoValueFtdSumTrend = useSelector(
    (state) => state?.dashBoardDetails?.wssAutoValueFtdTrend,
    (prev, next) => prev?.length === next?.length
  );

  return {
    wssAutoValueFtdSum,
    wssDarpanSyncDayExceptionCount,
    wssWssInBlockCount,
    wssAutoValueFtdSumTrend,
  };
};

/**
 * useHomeData Hook
 * Consolidated hook that fetches all home dashboard data
 * 
 * @returns {Object} All dashboard data organized by category
 */
export const useHomeData = () => {
  const salesGroupCode = useSelector(
    (state) => state?.userDetails?.details?.Sales_Group_Code__c,
    (prev, next) => prev === next
  );

  const secondarySales = useSecondarySalesData();
  const salesOperations = useSalesOperationsData();
  const team = useTeamData();
  const wss = useWssData();

  return {
    salesGroupCode,
    secondarySales,
    salesOperations,
    team,
    wss,
  };
};
